// Dependency-free helpers for reading/validating the auth JWT.

type JwtPayload = { exp?: number; [key: string]: unknown };

const decodeJwt = (token: string): JwtPayload | null => {
  try {
    const payload = token.split('.')[1];
    if (!payload) {
      return null;
    }
    // JWT uses base64url encoding; convert to standard base64 before decoding.
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
};

// Returns true when the token is missing, malformed, or past its `exp` claim.
export const isTokenExpired = (token: string | null): boolean => {
  if (!token) {
    return true;
  }
  const payload = decodeJwt(token);
  if (!payload || typeof payload.exp !== 'number') {
    // No usable expiry claim: treat a malformed token as expired, but let a
    // token without an `exp` claim through (nothing to validate against).
    return payload === null;
  }
  // `exp` is in seconds since epoch.
  return payload.exp * 1000 <= Date.now();
};

// Clears the stored session and sends the user to the login page.
export const forceLogout = (): void => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  if (window.location.pathname !== '/login') {
    window.location.assign('/login');
  }
};
